import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '~/lib/api';

interface InvoiceRecord {
  _id: string;
  email_from: string;
  email_subject: string;
  email_date: string;
  vendor_name?: string;
  amount?: number;
  currency?: string;
  status: string;
  source?: string;
}

interface DashboardData {
  membership: {
    monthly_usage: number;
    monthly_limit: number;
    usage_reset_at?: string;
    inbox_active: boolean;
    gmail_connected: boolean;
    gmail_status: string;
    netsuite_connected: boolean;
  };
  stats: {
    total: number;
    by_status: Record<string, number>;
    this_month: number;
  };
  recent_records: InvoiceRecord[];
}

const STATUS_COLORS: Record<string, string> = {
  pushed: 'badge-success',
  error: 'badge-error',
  classified: 'badge-info',
  triaging: 'badge-warning',
  extracting: 'badge-warning',
  needs_review: 'badge-warning',
  duplicate: 'badge-accent',
  quota_exceeded: 'badge-error',
  ignored: 'badge-ghost',
  received: 'badge-ghost',
};

const STATUS_LABELS: Record<string, string> = {
  pushed: 'enviada',
  error: 'error',
  needs_review: 'revisión',
  duplicate: 'duplicada',
  ignored: 'ignorada',
  triaging: 'clasificando',
  extracting: 'extrayendo',
  processing: 'procesando',
  quota_exceeded: 'cuota excedida',
};

function statusBadge(status: string) {
  const colorClass = STATUS_COLORS[status] || 'badge-ghost';
  const label = STATUS_LABELS[status] || status.replace(/_/g, ' ');
  return <span className={`badge badge-sm ${colorClass}`}>{label}</span>;
}

function fmtAmount(amount?: number, currency?: string) {
  if (amount == null) return '-';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: currency || 'COP', maximumFractionDigits: 0 }).format(amount);
}

export default function TbitInvoicesDashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<InvoiceRecord[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [inboxToggling, setInboxToggling] = useState(false);
  const [gmailLoading, setGmailLoading] = useState(false);
  const limit = 20;

  const handleInboxToggle = async () => {
    if (!data) return;
    setInboxToggling(true);
    try {
      const newActive = !data.membership.inbox_active;
      const res = await apiFetch('/tbit-invoices/api/config/inbox', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: newActive }),
      });
      if (res.ok) {
        setData(prev => prev ? { ...prev, membership: { ...prev.membership, inbox_active: newActive } } : prev);
      }
    } catch (err) {
      console.error('Failed to toggle inbox:', err);
    } finally {
      setInboxToggling(false);
    }
  };

  const handleGmailReauth = async () => {
    setGmailLoading(true);
    try {
      const res = await apiFetch('/tbit-invoices/auth/google-url');
      if (res.ok) {
        const { url } = await res.json();
        window.location.href = url;
      }
    } catch (err) {
      console.error('Failed to get Gmail auth URL:', err);
    } finally {
      setGmailLoading(false);
    }
  };

  useEffect(() => {
    apiFetch('/tbit-invoices/api/dashboard')
      .then(res => res.ok ? res.json() : null)
      .then(d => {
        if (d) {
          setData(d);
          setRecords(d.recent_records || []);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!data) return;
    apiFetch(`/tbit-invoices/api/records?page=${page}&limit=${limit}`)
      .then(res => res.json())
      .then(d => {
        if (d?.records) {
          setRecords(d.records);
          setTotalPages(Math.ceil((d.total || 0) / limit) || 1);
        }
      })
      .catch(console.error);
  }, [page, data]);

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Panel</h1>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="stat bg-base-100 rounded-box shadow animate-pulse">
              <div className="h-4 bg-base-300 rounded w-24 mb-2" />
              <div className="h-8 bg-base-300 rounded w-16 mb-1" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { membership, stats } = data;
  const { monthly_usage, monthly_limit } = membership;
  const usagePct = monthly_limit > 0 ? (monthly_usage / monthly_limit) * 100 : 0;
  const byStatus = stats.by_status || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Panel</h1>
        <div className="flex gap-2">
          <Link to="/tbit-invoices/gmail" className="btn btn-outline btn-sm">
            Importar de Gmail
          </Link>
          <Link to="/tbit-invoices/upload" className="btn btn-primary btn-sm">
            Subir factura
          </Link>
        </div>
      </div>

      {/* Gmail disconnected warning */}
      {!membership.gmail_connected && (
        <div className="alert alert-warning">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>Gmail desconectado. Reautoriza el acceso para continuar monitoreando facturas.</span>
          <button onClick={handleGmailReauth} disabled={gmailLoading} className="btn btn-sm btn-warning">
            {gmailLoading ? 'Redirigiendo...' : 'Reconectar Gmail'}
          </button>
        </div>
      )}

      {/* Inbox toggle */}
      <div className="card bg-base-100 shadow">
        <div className="card-body py-3 flex-row items-center justify-between">
          <div>
            <span className="font-semibold text-sm">Lectura de facturas</span>
            <span className="text-xs text-base-content/60 ml-2">
              {membership.inbox_active ? 'Activa — el sistema lee y procesa emails automáticamente' : 'Inactiva — no se procesan emails nuevos'}
            </span>
          </div>
          <input
            type="checkbox"
            className={`toggle toggle-primary ${inboxToggling ? 'opacity-50' : ''}`}
            checked={membership.inbox_active}
            onChange={handleInboxToggle}
            disabled={inboxToggling || !membership.gmail_connected}
          />
        </div>
      </div>

      {monthly_usage >= monthly_limit && (
        <div className="alert alert-error">
          <span>Cuota mensual excedida ({monthly_usage}/{monthly_limit}). Las nuevas facturas no se procesarán hasta que se reinicie la cuota.</span>
        </div>
      )}

      <div className="card bg-base-100 shadow">
        <div className="card-body py-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-semibold">Uso mensual</span>
            <span className="text-sm text-base-content/60">
              {monthly_usage} / {monthly_limit}
              {membership.usage_reset_at && (
                <> &middot; se reinicia el {new Date(membership.usage_reset_at).toLocaleDateString('es-CO')}</>
              )}
            </span>
          </div>
          <progress
            className={`progress w-full ${usagePct > 90 ? 'progress-error' : 'progress-primary'}`}
            value={monthly_usage}
            max={monthly_limit}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Total</div>
          <div className="stat-value text-primary">{stats.total}</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Enviadas</div>
          <div className="stat-value text-success">{byStatus.pushed || 0}</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Errores</div>
          <div className="stat-value text-error">{byStatus.error || 0}</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Revisión</div>
          <div className="stat-value text-warning">{byStatus.needs_review || 0}</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Duplicadas</div>
          <div className="stat-value text-accent">{byStatus.duplicate || 0}</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Ignoradas</div>
          <div className="stat-value text-base-content/50">{byStatus.ignored || 0}</div>
        </div>
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title">Facturas</h2>
          {records.length === 0 ? (
            <p className="text-base-content/50">
              No hay facturas procesadas. Configura tu bandeja de entrada o sube una factura para comenzar.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Proveedor</th>
                      <th>Asunto</th>
                      <th>Monto</th>
                      <th>Estado</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map(record => (
                      <tr key={record._id}>
                        <td className="text-xs whitespace-nowrap">
                          {new Date(record.email_date).toLocaleDateString('es-CO')}
                        </td>
                        <td className="text-sm max-w-[200px] truncate">{record.vendor_name || record.email_from}</td>
                        <td className="text-sm max-w-[250px] truncate">{record.email_subject}</td>
                        <td className="text-sm font-mono">{fmtAmount(record.amount, record.currency)}</td>
                        <td>{statusBadge(record.status)}</td>
                        <td>
                          <Link to={`/tbit-invoices/records/${record._id}`} className="btn btn-ghost btn-xs">
                            Ver
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <button className="btn btn-sm btn-ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    &larr; Anterior
                  </button>
                  <span className="flex items-center text-sm text-base-content/60">
                    Página {page} de {totalPages}
                  </span>
                  <button className="btn btn-sm btn-ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                    Siguiente &rarr;
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
